import * as React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';
import { searchSmsMessages, SmsMessage, SmsMessagesSearchParams } from '../../api/bulkSms';
import SmsMessageDetailsDialog from './components/SmsMessageDetailsDialog';

const DELIVERY_STATUSES = ['Sent', 'Submitted', 'Buffered', 'Rejected', 'Success', 'Failed', 'AbsentSubscriber', 'Expired'];
const BATCH_STATUSES = ['PENDING', 'PROCESSING', 'SUCCESS', 'FAILED', 'CANCELLED'];
const PURPOSES = ['MARKETING', 'REMINDERS', 'OTP', 'ALERTS'];
const BATCH_TYPES = ['INSTANT', 'SCHEDULED'];

function statusColor(status: string | null | undefined): 'success' | 'error' | 'warning' | 'default' {
  if (!status) return 'default';
  const s = status.toUpperCase();
  if (s === 'SUCCESS' || s === 'SENT') return 'success';
  if (s === 'FAILED' || s === 'REJECTED' || s === 'EXPIRED' || s === 'ABSENTSUBSCRIBER') return 'error';
  if (s === 'PENDING' || s === 'PROCESSING' || s === 'SUBMITTED' || s === 'BUFFERED') return 'warning';
  return 'default';
}

export default function BulkSmsHistoryPage() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = React.useState<SmsMessagesSearchParams>(() => ({
    batchType: searchParams.get('batchType') ?? undefined,
  }));
  const [items, setItems] = React.useState<SmsMessage[]>([]);
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [hasNext, setHasNext] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = React.useState<SmsMessage | null>(null);

  const runSearch = async (nextFilters: SmsMessagesSearchParams, append: boolean) => {
    setLoading(true);
    setError(null);
    const res = await searchSmsMessages({ ...nextFilters, cursor: append ? cursor : undefined });
    setLoading(false);
    if (!res.success || !res.data) {
      setError(res.message ?? 'Failed to load SMS history.');
      return;
    }
    setItems((prev) => (append ? [...prev, ...res.data!.items] : res.data!.items));
    setHasNext(res.data.hasNext);
    setCursor(res.data.nextCursor);
  };

  React.useEffect(() => {
    runSearch(filters, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (key: keyof SmsMessagesSearchParams, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
  };

  const handleSearch = () => {
    setCursor(undefined);
    runSearch(filters, false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>SMS Delivery Status</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search sent SMS messages and check per-recipient delivery status.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              label="Contact number"
              value={filters.contactNumber ?? ''}
              onChange={(e) => handleFilterChange('contactNumber', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Delivery status"
              value={filters.deliveryStatus ?? ''}
              onChange={(e) => handleFilterChange('deliveryStatus', e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {DELIVERY_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Batch status"
              value={filters.batchStatus ?? ''}
              onChange={(e) => handleFilterChange('batchStatus', e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {BATCH_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Purpose"
              value={filters.purpose ?? ''}
              onChange={(e) => handleFilterChange('purpose', e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {PURPOSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              select
              fullWidth
              size="small"
              label="Batch type"
              value={filters.batchType ?? ''}
              onChange={(e) => handleFilterChange('batchType', e.target.value)}
            >
              <MenuItem value="">Any</MenuItem>
              {BATCH_TYPES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="From"
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleFilterChange('deliveryTimeFrom', e.target.value ? new Date(e.target.value).toISOString() : '')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              type="datetime-local"
              label="To"
              InputLabelProps={{ shrink: true }}
              onChange={(e) => handleFilterChange('deliveryTimeTo', e.target.value ? new Date(e.target.value).toISOString() : '')}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
            <Button variant="contained" onClick={handleSearch} disabled={loading} fullWidth>
              {loading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Contact</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Network</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Purpose</TableCell>
              <TableCell>Delivery time</TableCell>
              <TableCell>Message</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => setSelectedMessage(row)}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>{row.contact}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.deliveryStatus ?? 'Unknown'} color={statusColor(row.deliveryStatus)} />
                </TableCell>
                <TableCell>{row.deliveryNetwork}</TableCell>
                <TableCell>{row.batchId} ({row.batchStatus})</TableCell>
                <TableCell>{row.batchPurpose}</TableCell>
                <TableCell>{row.deliveryTime ? new Date(row.deliveryTime).toLocaleString() : '—'}</TableCell>
                <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.message}
                </TableCell>
              </TableRow>
            ))}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>No messages found.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {hasNext && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Button onClick={() => runSearch(filters, true)} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Load more'}
          </Button>
        </Stack>
      )}

      <SmsMessageDetailsDialog
        open={!!selectedMessage}
        message={selectedMessage}
        onClose={() => setSelectedMessage(null)}
      />
    </Box>
  );
}
